import { useState, useRef, useEffect } from "react";
import { MessageCircle, X, Send, Bot } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function generateSessionId() {
  return "chat_" + Math.random().toString(36).substring(2, 15);
}

export default function ChatbotWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content: "Bonjour et bienvenue chez IGN Services Alsace. Je peux vous aider à trouver la prestation adaptée à votre besoin. Souhaitez-vous un renseignement ou un devis ?",
    },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(generateSessionId);
  const messagesEnd = useRef(null);

  useEffect(() => {
    messagesEnd.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const sendMessage = async () => {
    if (!input.trim() || loading) return;

    const userMsg = input.trim();
    setInput("");
    setMessages((prev) => [...prev, { role: "user", content: userMsg }]);
    setLoading(true);

    try {
      const res = await axios.post(`${API}/chat`, {
        session_id: sessionId,
        message: userMsg,
      });
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: res.data.response },
      ]);
    } catch {
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: "Désolé, une erreur est survenue. Vous pouvez nous contacter directement au 07 66 91 68 96.",
        },
      ]);
    }
    setLoading(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      {/* Chat bubble button */}
      {!open && (
        <button
          data-testid="chatbot-toggle"
          onClick={() => setOpen(true)}
          className="fixed bottom-24 right-6 z-[9999] w-16 h-16 bg-[#0A2540] text-white flex items-center justify-center shadow-2xl chat-bubble hover:bg-[#C22D2D] transition-colors duration-300"
          style={{ borderRadius: "50% 50% 8px 50%" }}
        >
          <MessageCircle className="w-7 h-7" />
        </button>
      )}

      {/* Chat window */}
      {open && (
        <div
          data-testid="chatbot-window"
          className="fixed bottom-24 right-6 z-[9999] w-[380px] max-w-[calc(100vw-48px)] bg-white border border-[#E2E8F0] shadow-2xl flex flex-col"
          style={{ height: "520px" }}
        >
          {/* Header */}
          <div className="bg-[#0A2540] text-white px-5 py-4 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-[#C22D2D] flex items-center justify-center rounded-full">
                <Bot className="w-5 h-5" />
              </div>
              <div>
                <p className="text-sm font-semibold font-['Outfit']">IGN Services</p>
                <p className="text-[10px] text-white/60 font-['Manrope']">Assistant en ligne</p>
              </div>
            </div>
            <button
              data-testid="chatbot-close"
              onClick={() => setOpen(false)}
              className="p-1 hover:bg-white/10 transition-colors rounded"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
            {messages.map((msg, idx) => (
              <div
                key={idx}
                data-testid={`chat-message-${idx}`}
                className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[85%] px-4 py-3 text-sm leading-relaxed font-['Manrope'] ${
                    msg.role === "user"
                      ? "bg-[#0A2540] text-white"
                      : "bg-[#F8F9FA] text-[#1E293B] border border-[#E2E8F0]"
                  }`}
                >
                  {msg.content}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-[#F8F9FA] text-[#64748B] border border-[#E2E8F0] px-4 py-3 text-sm font-['Manrope']">
                  <span className="inline-flex gap-1">
                    <span className="w-2 h-2 bg-[#0A2540] rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                    <span className="w-2 h-2 bg-[#0A2540] rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                    <span className="w-2 h-2 bg-[#0A2540] rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                  </span>
                </div>
              </div>
            )}
            <div ref={messagesEnd} />
          </div>

          {/* Input */}
          <div className="border-t border-[#E2E8F0] px-4 py-3 flex-shrink-0">
            <div className="flex items-center gap-2">
              <input
                data-testid="chatbot-input"
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Votre message..."
                className="flex-1 border border-[#E2E8F0] bg-[#F8F9FA] px-4 py-2.5 text-sm text-[#1E293B] focus:outline-none focus:border-[#0A2540] font-['Manrope']"
              />
              <button
                data-testid="chatbot-send"
                onClick={sendMessage}
                disabled={!input.trim() || loading}
                className="w-10 h-10 flex items-center justify-center bg-[#0A2540] text-white hover:bg-[#C22D2D] transition-colors duration-300 disabled:opacity-40"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
